import * as React from "react"
import { type LayoutComponent, LayoutComponentType } from "@digits-graphql/frontend/graphql-bearer"
import { SvgXClose } from "@digits-shared/components/SVGIcons/line/XClose.svg"
import { DigitsButton } from "@digits-shared/DesignSystem/Button"
import colors from "@digits-shared/themes/colors"
import styled from "styled-components"
import { type ComponentSize } from "src/frontend/components/Shared/Layout/ComponentSize"
import { matchComponent } from "src/frontend/components/Shared/Layout/types"

/*
  STYLES
*/

const ActionsButtons = styled.div`
  display: flex;
  gap: 8px;

  position: absolute;
  top: -8px;
  right: -8px;
`

const ActionButton = styled(DigitsButton)`
  box-shadow: 0 0 5px 0 ${colors.secondary30};
`

/*
  INTERFACES
*/

interface ComponentControlsProps {
  className?: string
  component: LayoutComponent
  componentSize: ComponentSize

  onDeleteComponent?: (cId: string) => void
}

interface ActionsButtonsProps extends ComponentControlsProps {
  isDragging?: boolean
  isEditing?: boolean
}

/*
  COMPONENTS
*/

export const ComponentActionsButtons: React.FC<ActionsButtonsProps> = ({
  className,
  component,
  componentSize,
  onDeleteComponent,
  isDragging,
  isEditing,
}) => (
  <>
    {isEditing && !isDragging && (
      <ComponentEditActions
        className={className}
        component={component}
        componentSize={componentSize}
        onDeleteComponent={onDeleteComponent}
      />
    )}
    {!isEditing && !isDragging && (
      <ComponentViewActions
        className={className}
        component={component}
        componentSize={componentSize}
        onDeleteComponent={onDeleteComponent}
      />
    )}
  </>
)

const ComponentViewActions: React.FC<ComponentControlsProps> = ({
  className,
  component,
  componentSize,
  onDeleteComponent,
}) => {
  const isStatement = matchComponent(component, "statement", LayoutComponentType.Statement)
  if (!isStatement) return null

  return <ActionsButtons className={className}></ActionsButtons>
}

const ComponentEditActions: React.FC<ComponentControlsProps> = ({
  className,
  component,
  componentSize,
  onDeleteComponent,
}) => {
  const onDeleteClick = React.useCallback(() => {
    onDeleteComponent?.(component.componentId)
  }, [component.componentId, onDeleteComponent])

  return (
    <ActionsButtons className={className}>
      <ActionButton size="medium" $variant="white" $circle onClick={onDeleteClick}>
        <SvgXClose />
      </ActionButton>
    </ActionsButtons>
  )
}
